<script setup lang="ts">
import { computed, ref, useSlots, watch } from 'vue';
import type { RouteLocationRaw } from 'vue-router';
import { storeToRefs } from 'pinia';
import { AppLink } from '@sunlabde/m2m-library';
import { useBreakpoints } from '@vueuse/core';
import { useSignupNavigation } from '@/utilities/Navigation';
import { useStaticLinkComposer } from '@/utilities/StaticLinks';
import router from '@/router';
import { useProductsStore } from '@/store/products';
import { useMainStore } from '@/store/main';
import { globalBreakpoints } from '@/utilities/Breakpoints';

withDefaults(defineProps<{
	isBodyVisible?: boolean;
}>(), {
	isBodyVisible: true,
});

const slots = useSlots();

const formatUrl = (location: RouteLocationRaw): string => {
	if (typeof location === 'string') {
		return location;
	}

	return router.resolve(location).href;
};

const signupNavigationItems = useSignupNavigation().navigation; // .value.map(createSignupLinkNavigationItem);
const staticLinkComposer = useStaticLinkComposer();

const { signupStyle } = storeToRefs(useMainStore());

const productsStore = useProductsStore();
const { portal } = storeToRefs(productsStore);

const hasBodySlot = ref(false);
const hasFooterSlot = ref(false);

const breakpoints = useBreakpoints(globalBreakpoints);

watch(() => breakpoints.active().value, () => {
	hasBodySlot.value = !!slots.body?.()?.length;
	hasFooterSlot.value = !!slots.body?.()?.length;
},
{ immediate: true },
);

const isThomannStyled = computed(() => signupStyle.value === 'thomann');
const portalLogo = computed(() => portal.value?.options.logo);
</script>

<template>
	<div class="aside relative flex flex-col bg-center bg-cover desktop:py-8 box-border text-white order-2 phablet:px-20 desktop:px-12 py-6 desktop:fixed desktop:top-0 desktop:left-0 desktop:h-screen desktop:w-[600px]  bg-secondary-950 overflow-hidden">
		<div
			class="block bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-secondary-500/35 via-transparent to-transparent absolute z-0 -bottom-[600px] w-[1400px] h-[1400px] -right-[800px] phablet:-right-[600px] desktop:-bottom-[900px] desktop:-left-[900px] desktop:h-[1800px] desktop:w-[1800px]"
		/>
		<div class="z-[1] items-center mb-12 desktop:mb-16 hidden desktop:flex">
			<a
				:href="staticLinkComposer.compose('home')"
				class="flex-shrink-0 max-w-[140px] max-h-[70px]"
			>
				<img
					src="@/assets/logo.svg"
					alt="logo"
				>
			</a>
			<a
				v-if="isThomannStyled"
				class="flex-shrink-0 w-full max-w-[140px] max-h-[70px] border-l pl-4 ml-4 h-full flex items-center"
				href="https://www.thomann.de"
				rel="noopener noreferrer"
				target="_blank"
			>
				<img
					src="@/assets/thomann-cyan-white.svg"
					height="30"
					alt="thomann"
				>
			</a>
			<img
				v-if="portalLogo"
				:src="portalLogo"
				:alt="portalLogo"
				class="flex-shrink-0 max-w-[140px] max-h-[70px] border-r-[1px] border-r-[color:white] pr-4 mr-4"
			>
		</div>
		<div
			v-if="hasBodySlot"
			class="z-[1] max-w-[950px] w-full mx-auto desktop:order-none order-2 h-full"
		>
			<slot name="body" />
		</div>
		<div
			v-if="hasFooterSlot"
			class="flex flex-col items-stretch justify-end flex-grow z-[1] max-w-[950px] w-full mx-auto order-1 desktop:order-none px-6 desktop:px-0"
		>
			<slot name="footer" />
		</div>
		<div
			:class="{ 'mt-16': (hasBodySlot || hasFooterSlot) && isBodyVisible }"
			class="z-[1] max-w-[950px] w-full mx-auto text-xs order-3 px-6 desktop:hidden"
		>
			<AppLink
				v-for="item in signupNavigationItems"
				:key="item.name"
				:link="formatUrl(item.link)"
				class="text-white hover:text-primary-400 mr-2 last:mr-0"
				target="_blank"
			>
				{{ item.name }}
			</AppLink>
		</div>
	</div>
</template>
